import mapboxgl from 'maplibre-gl';

/**
 *
 * @param {Array} center
 */
export function getPoint(center) {
  return {
    type: 'Feature',
    geometry: {
      type: 'Point',
      coordinates: center,
    },
    properties: {
    },
  };
}

/**
 *
 * @param {Object} point
 */
function geoFeature(point) {
  return {
    type: 'Feature',
    geometry: {
      type: 'Point',
      coordinates: point.coords,
    },
    properties: {
      desc: point.label,
      icon: 'default',
    },
  };
}

/**
 *
 * @param {Array} points
 * @param {Number} clusterRadius
 */
function mapSource(points, clusterRadius) {
  const features = [];

  points.forEach((point) => {
    features.push(geoFeature(point));
    // ajout de l'id des ADJ dans les features
    features[features.length - 1].properties.id = point.id;
    //
  });
  return {
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      features,
    },
    cluster: true,
    clusterMaxZoom: 14,
    clusterRadius,
  };
}

/**
 *
 * @param {String} clusterName
 */
function layerOutlines(clusterName) {
  return {
    id: `${clusterName}-outline`,
    type: 'circle',
    source: clusterName,
    filter: ['has', 'point_count'],
    paint: {
      'circle-color': {
        property: 'point_count',
        type: 'interval',
        stops: [
          [0, 'rgba(68, 216, 240, 0.6)'],
          [10, 'rgba(241, 211, 87, 0.6)'],
          [100, 'rgba(253, 156, 115, 0.6)'],
        ],
      },
      'circle-radius': 20,
    },
  };
}

/**
 *
 * @param {String} clusterName
 */
function layerCircles(clusterName) {
  return {
    id: `${clusterName}-circles`,
    type: 'circle',
    source: clusterName,
    filter: ['has', 'point_count'],
    paint: {
      'circle-color': {
        property: 'point_count',
        type: 'interval',
        stops: [
          [0, 'rgba(0, 186, 212, 0.6)'],
          [10, 'rgba(240, 194, 12, 0.6)'],
          [100, 'rgba(241, 128, 23, 0.6)'],
        ],
      },
      'circle-radius': 15,
    },
  };
}

/**
 *
 * @param {String} clusterName
 */
function layerCounts(clusterName) {
  return {
    id: `${clusterName}-count`,
    type: 'symbol',
    source: clusterName,
    filter: ['has', 'point_count'],
    layout: {
      // "text-field": "{point_count_abbreviated}",
      'text-field': '{point_count}',
      'text-font': ['Open Sans Regular'],
      'text-size': 12,
    },
  };
}

/**
 *
 * @param {String} clusterName
 * @param {Object} icon
 */
function layerMarkers(clusterName, icon) {
  const l = icon.width * icon.left;
  const t = icon.height * -icon.top;

  return {
    id: `${clusterName}-markers`,
    type: 'symbol',
    source: clusterName,
    filter: ['!has', 'point_count'],
    layout: {
      'icon-image': icon.label,
      'icon-offset': [l, t],
    },
  };
}

/**
 *
 * @param {Object} map
 * @param {Array} images
 * @param {Function} callback
 */
function loadImages(map, images, callback) {
  let cur = 0;
  const max = images.length;

  function loadImage(localmap, img) {
    if (localmap.hasImage(img.label)) {
      localmap.removeImage(img.label);
    }
    localmap.loadImage(img.url, (err, image) => {
      if (err) throw err;
      localmap.addImage(img.label, image);
      cur += 1;
      if (cur === max) callback();
    });
  }

  images.forEach((image) => {
    loadImage(map, image);
  });
}

/**
 *
 * @param {Object} map
 * @param {Object} options
 */
export function addMarker(map, options) {
  map.loadImage(options.icon.url, (err, image) => {
    if (err) throw err;

    const label = options.icon.label ? options.icon.label : 'default';
    const l = options.icon.width * options.icon.left;
    const t = options.icon.height * -options.icon.top;

    map.addImage(`${label}-img`, image);

    map.addSource(`${label}-src`, {
      type: 'geojson',
      data: getPoint([options.lng, options.lat]),
    });

    map.addLayer({
      id: `${label}-layer`,
      type: 'symbol',
      source: `${label}-src`,
      layout: {
        'icon-image': `${label}-img`,
        'icon-offset': [l, t],
        'icon-allow-overlap': true,
      },
    });

    if (options.popup) {
      // Pop up offset
      const popupOffset = [
        options.icon.height * options.popup.left, options.icon.height * -options.popup.top,
      ];

      // Marker popup
      map.on('click', `${label}-layer`, (e) => {
        new mapboxgl.Popup({
          offset: popupOffset,
        })
          .setLngLat(e.features[0].geometry.coordinates)
          .setHTML(options.popup.html)
          .addTo(map);
        return false;
      });

      // Change the cursor to a pointer when the mouse is over the places layer.
      map.on('mouseenter', `${label}-layer`, () => {
        map.getCanvas().style.cursor = 'pointer';
      });

      // Change it back to a pointer when it leaves.
      map.on('mouseleave', `${label}-layer`, () => {
        map.getCanvas().style.cursor = '';
      });
    }
  });
}

/**
 *
 * @param {Object} map
 * @param {Array} points
 * @param {Object} options
 */
export function makeCluster(map, points, options) {
  const clusterName = 'cluster';
  const clusterMarkers = `${clusterName}-markers`;
  const clusterOutline = `${clusterName}-outline`;

  const images = [];
  images.push(options.icon);

  loadImages(map, images, () => {
    map.addSource(clusterName, mapSource(points, options.clusterRadius));
    map.addLayer(layerOutlines(clusterName));
    map.addLayer(layerCircles(clusterName));
    map.addLayer(layerCounts(clusterName));
    map.addLayer(layerMarkers(clusterName, options.icon));

    // Change the cursor to a pointer when the mouse is over the places layer.
    map.on('mouseenter', clusterMarkers, () => {
      map.getCanvas().style.cursor = 'pointer';
    });

    // Change it back to a pointer when it leaves.
    map.on('mouseleave', clusterMarkers, () => {
      map.getCanvas().style.cursor = '';
    });

    // Zoom
    map.on('click', clusterOutline, (e) => {
      map.easeTo({
        center: e.lngLat,
        zoom: map.getZoom() + 1,
      });
    });

    // Change the cursor to a pointer when the mouse is over the places layer.
    map.on('mouseenter', clusterOutline, () => {
      map.getCanvas().style.cursor = 'pointer';
    });

    // Change it back to a pointer when it leaves.
    map.on('mouseleave', clusterOutline, () => {
      map.getCanvas().style.cursor = '';
    });
  });
}

/**
 *
 * @param {Object} map
 * @param {Array} coordinates
 * @param {String} type
 */
export function displayBound(map, coordinates, type) {
  map.addSource('bound', {
    type: 'geojson',
    data: {
      type: 'Feature',
      geometry: {
        type,
        coordinates,
      },
    },
  });

  map.addLayer({
    id: 'bound-fill',
    type: 'fill',
    source: 'bound',
    layout: {},
    paint: {
      'fill-color': '#ffaaaa',
      'fill-opacity': 0.3,
    },
  });

  map.addLayer({
    id: 'bound-borders',
    type: 'line',
    source: 'bound',
    layout: {},
    paint: {
      'line-color': '#D46A6A',
      'line-width': 2,
      'line-opacity': 0.8,
    },
  });
}
